// =============================================================================
// src/features/analytics/analytics-controller.ts - Estado local do Analytics.
// =============================================================================

import { qs, qsa } from '../../app/dom';
import { showMiniToast } from '../../chrome/mini-toast/index';
import {
  OMAR_ANALYTICS_MODULES,
  type OmarAnalyticsModuleName,
  type OmarAnalyticsSortName,
} from './analytics-data';
import { analyticsRawPlatformsForUiLabel } from '../../data/analytics-overlay-format';
import { ANALYTICS_SCOPE_LABELS } from '../../data/analytics-scope-where';
import { analyticsActorFeedAvailable, loadAnalyticsActorFeed } from './analytics-actor-feed-store';
import {
  analyticsFeedPageAvailable,
  requestAnalyticsFeedPage,
} from './analytics-feed-page-store';
import { buildAnalyticsFeedPaginationMarkup } from './analytics-feed-pagination-markup';
import { analyticsFeedPageView, analyticsListedMentions } from './analytics-mention-markup';
import {
  activeAnalyticsDatasetValue,
  setAnalyticsPressedGroup,
  syncAnalyticsButtonsByValue,
} from './analytics-dom-sync';
import { submitAnalyticsAuthorBlock } from './analytics-block-store';
import {
  type AnalyticsScopeOverrideOutcome,
  submitAnalyticsScopeOverride,
} from './analytics-scope-store';
import {
  analyticsEmptyLiveFields,
  analyticsLiveSource,
  getAnalyticsActorTotals,
  getAnalyticsFeedItems,
} from './analytics-live-store';
import { bindAnalyticsViewControls } from './analytics-view-controls';
import { getAnalyticsViewState } from './analytics-view-state';
import { buildAnalyticsDetailCardsMarkup, buildAnalyticsFeedCardsMarkup } from './analytics-mention-markup';
import { type OmarAnalyticsMention } from './analytics-data';
import { bindAnalyticsChartControls } from './analytics-chart-controller';
import {
  hideAnalyticsMotionElement,
  showAnalyticsMotionElement,
} from './analytics-motion';

interface AnalyticsFilterState {
  readonly actor: string;
  readonly relation: string;
  readonly platform: string;
  readonly sentiment: string;
  readonly siteKind: string;
  readonly search: string;
}

interface AnalyticsRuntimeState {
  moduleName: OmarAnalyticsModuleName;
  sortName: OmarAnalyticsSortName;
  detailMentionId: string;
  /** Slug do personagem em drill-down ('Todos' = feed base). */
  actorSlug: string;
  /** Tipo de relação em drill-down ('all' | publicacao | marcacao | mencao). */
  relation: string;
  /** true quando o feed exibido veio de um drill-down no servidor. */
  feedDrilled: boolean;
  /** Trava anti-corrida do drill-down (um fetch por vez). */
  actorFeedLoading: boolean;
}

type AnalyticsPanelSide = 'left' | 'right';

const DEFAULT_ANALYTICS_STATE: AnalyticsRuntimeState = {
  moduleName: 'mentions',
  sortName: 'recent',
  detailMentionId: '',
  actorSlug: 'Todos',
  relation: 'all',
  feedDrilled: false,
  actorFeedLoading: false,
};

let analyticsRuntimeState: AnalyticsRuntimeState = { ...DEFAULT_ANALYTICS_STATE };

/** Modulo ativo — o re-render por escopo/periodo devolve o operador a ele. */
export function activeAnalyticsModuleName(): OmarAnalyticsModuleName {
  return analyticsRuntimeState.moduleName;
}

/** Liga modulos, filtros, busca, detalhe, ordenacao, modal e rails. */
export function initAnalyticsInteractions(initialModule: OmarAnalyticsModuleName = 'mentions'): void {
  analyticsRuntimeState = { ...DEFAULT_ANALYTICS_STATE };
  // Escopo/periodo/reacoes (2026-09-02): recorte no servidor + re-render.
  bindAnalyticsViewControls({ ensureMentionsModule: () => showAnalyticsModule('mentions') });
  bindAnalyticsModuleButtons();
  bindAnalyticsFilterButtons();
  bindAnalyticsChartControls();
  bindAnalyticsMentionDetails();
  bindAnalyticsModuleShortcuts();
  bindAnalyticsExpandedRows();
  bindAnalyticsPanelToggles();
  bindAnalyticsLocalActions();
  showAnalyticsModule(initialModule);
  applyAnalyticsFeedFilters();
}


function bindAnalyticsModuleButtons(): void {
  qsa<HTMLButtonElement>('[data-analytics-module]').forEach((moduleButton) => {
    moduleButton.addEventListener('click', () => {
      const moduleName = parseAnalyticsModuleName(moduleButton.dataset.analyticsModule);
      if (moduleName) showAnalyticsModule(moduleName);
    });
  });
}

function bindAnalyticsFilterButtons(): void {
  qs<HTMLInputElement>('#analyticsSearchInput')?.addEventListener('input', applyAnalyticsFeedFilters);
  qsa<HTMLButtonElement>('[data-analytics-platform-filter]').forEach(bindAnalyticsPlatformButton);
  qsa<HTMLButtonElement>('[data-analytics-actor-filter]').forEach(bindAnalyticsActorButton);
  qsa<HTMLButtonElement>('[data-analytics-relation-filter]').forEach(bindAnalyticsRelationButton);
  qsa<HTMLButtonElement>('[data-analytics-clear-filters]').forEach(bindAnalyticsClearFiltersButton);
  qsa<HTMLButtonElement>('[data-analytics-sentiment-filter]').forEach(bindAnalyticsSentimentButton);
  qsa<HTMLButtonElement>('[data-analytics-site-kind-filter]').forEach(bindAnalyticsSiteKindButton);
  qsa<HTMLButtonElement>('[data-analytics-block-author]').forEach(bindAnalyticsBlockAuthorButton);
  qsa<HTMLButtonElement>('[data-analytics-scope-override]').forEach(bindAnalyticsScopeOverrideButton);
}

/** "Fora de escopo" REAL: grava o override por CONTEUDO e some com o card. O
 * content_relevance_scoring respeita a decisao humana acima de qualquer regra. */
function bindAnalyticsScopeOverrideButton(scopeButton: HTMLButtonElement): void {
  scopeButton.addEventListener('click', () => {
    /* v8 ignore next -- o attr carrega SEMPRE o mentionId no botao live; ?? exigido pelo tipo de dataset */
    const contentId = scopeButton.dataset.analyticsScopeOverride ?? '';
    scopeButton.disabled = true;
    void submitAnalyticsScopeOverride({ contentId, verdict: 'descartado', reason: 'curadoria via Analytics' })
      .then((scopeOutcome) => finishAnalyticsScopeOverride(scopeButton, contentId, scopeOutcome));
  });
}

function finishAnalyticsScopeOverride(
  scopeButton: HTMLButtonElement,
  contentId: string,
  scopeOutcome: AnalyticsScopeOverrideOutcome,
): void {
  if (scopeOutcome !== 'saved') {
    scopeButton.disabled = false;
    showMiniToast('Nao foi possivel marcar agora - tente de novo', 'ti-alert-triangle');
    return;
  }
  removeAnalyticsMentionCard(contentId);
  applyAnalyticsFeedFilters();
  showMiniToast('Conteudo marcado fora de escopo - nao voltara ao painel', 'ti-map-off');
}

/** Remove o card e o detalhe pre-renderizado de UM conteudo curado. */
function removeAnalyticsMentionCard(contentId: string): void {
  qs<HTMLElement>(`[data-analytics-detail="${contentId}"]`)?.remove();
  qsa<HTMLElement>(`[data-analytics-open-mention="${contentId}"]`).forEach((openButton) => {
    openButton.closest('[data-analytics-feed]')?.remove();
  });
}

/** Bloqueio REAL de autor (curadoria de falso positivo): grava na blocklist e
 * remove os cards do autor; as views de serving garantem que nao volta. */
function bindAnalyticsBlockAuthorButton(blockButton: HTMLButtonElement): void {
  blockButton.addEventListener('click', () => {
    /* v8 ignore next 2 -- os 2 attrs sao SEMPRE renderizados juntos no botao live; ?? exigido pelo tipo de dataset */
    const authorHandle = blockButton.dataset.analyticsBlockAuthor ?? '';
    const rawPlatform = blockButton.dataset.analyticsBlockPlatform ?? '';
    blockButton.disabled = true;
    void submitAnalyticsAuthorBlock({ platform: rawPlatform, authorHandle, reason: 'curadoria via Analytics' })
      .then((blockOutcome) => finishAnalyticsAuthorBlock(blockButton, authorHandle, blockOutcome));
  });
}

function finishAnalyticsAuthorBlock(
  blockButton: HTMLButtonElement,
  authorHandle: string,
  blockOutcome: 'blocked' | 'failed' | 'unavailable',
): void {
  if (blockOutcome !== 'blocked') {
    blockButton.disabled = false;
    showMiniToast('Nao foi possivel bloquear agora - tente de novo', 'ti-alert-triangle');
    return;
  }
  removeAnalyticsAuthorCards(authorHandle);
  applyAnalyticsFeedFilters();
  showMiniToast(`@${authorHandle} bloqueado - conteudos dele nao voltarao a aparecer`, 'ti-ban');
}

/** Remove cards do feed e os detalhes pre-renderizados do autor bloqueado. */
function removeAnalyticsAuthorCards(authorHandle: string): void {
  qsa<HTMLElement>(`[data-analytics-feed][data-analytics-author-handle="${authorHandle}"]`).forEach((mentionCard) => {
    const mentionId = mentionCard.querySelector<HTMLElement>('[data-analytics-open-mention]')?.dataset.analyticsOpenMention;
    if (mentionId) qs<HTMLElement>(`[data-analytics-detail="${mentionId}"]`)?.remove();
    mentionCard.remove();
  });
}

function bindAnalyticsPlatformButton(platformButton: HTMLButtonElement): void {
  platformButton.addEventListener('click', () => {
    syncAnalyticsButtonsByValue('data-analytics-platform-filter', platformButton.dataset.analyticsPlatformFilter);
    // O filtro de canal age sobre o FEED: fora da view de mencoes o clique nao
    // teria efeito visivel (parecia quebrado — feedback do usuario 2026-07-25).
    if (analyticsRuntimeState.moduleName !== 'mentions') showAnalyticsModule('mentions');
    // Escopo ativo (personagem/tipo): canal tambem refaz a busca no servidor.
    onAnalyticsFeedScopeChange();
  });
}

/** Chips de personagem: no live, clicar BUSCA o conteudo real do ator no
 * servidor (a amostra local nao limita — caso Omar×Instagram=0 com 62 no
 * banco); 'Todos' volta ao feed base. Sem loader (mock), filtro local. */
function bindAnalyticsActorButton(actorButton: HTMLButtonElement): void {
  actorButton.addEventListener('click', () => {
    /* v8 ignore next -- chip sempre carrega o slug no attr; ?? exigido pelo tipo de dataset */
    const actorSlug = actorButton.dataset.analyticsActorFilter ?? 'Todos';
    syncAnalyticsButtonsByValue('data-analytics-actor-filter', actorSlug);
    analyticsRuntimeState.actorSlug = actorSlug;
    onAnalyticsFeedScopeChange();
  });
}

/** Filtro de TIPO da relação ator↔conteúdo: publicação × marcação × menção. */
function bindAnalyticsRelationButton(relationButton: HTMLButtonElement): void {
  relationButton.addEventListener('click', () => {
    /* v8 ignore next -- botao sempre carrega o valor no attr; ?? exigido pelo tipo de dataset */
    const relation = relationButton.dataset.analyticsRelationFilter ?? 'all';
    syncAnalyticsButtonsByValue('data-analytics-relation-filter', relation);
    analyticsRuntimeState.relation = relation;
    if (analyticsRuntimeState.moduleName !== 'mentions') showAnalyticsModule('mentions');
    onAnalyticsFeedScopeChange();
  });
}

/** Decisor único do escopo do feed: personagem OU tipo ativos (live) →
 * drill-down no servidor; escopo neutro → restaura o feed base local. */
function onAnalyticsFeedScopeChange(): void {
  const scopeIsNeutral = analyticsRuntimeState.actorSlug === 'Todos' && analyticsRuntimeState.relation === 'all';
  if (!scopeIsNeutral && analyticsActorFeedAvailable()) {
    void refetchAnalyticsActorFeed();
    return;
  }
  if (analyticsRuntimeState.feedDrilled && scopeIsNeutral) {
    analyticsRuntimeState.feedDrilled = false;
    renderAnalyticsFeedCards(getAnalyticsFeedItems());
  }
  applyAnalyticsFeedFilters();
}

/**
 * Pede uma PAGINA do feed e re-renderiza a lista + a nav (2026-09-20).
 *
 * Exportada porque o composition root a chama no bootstrap: sem a primeira busca
 * a lista ao vivo nasceria VAZIA — `analyticsListedMentions()` passa a ler a
 * pagina servida assim que o loader existe.
 */
export async function refreshAnalyticsFeedPage(page: number, refreshTotal: boolean): Promise<void> {
  if (!analyticsFeedPageAvailable()) return;
  renderAnalyticsFeedPagination();                       // trava a nav enquanto busca
  const hasActor = analyticsRuntimeState.actorSlug !== 'Todos';
  const hasRelation = analyticsRuntimeState.relation !== 'all';
  const outcome = await requestAnalyticsFeedPage({
    page,
    rawPlatforms: analyticsRawPlatformsForUiLabel(activeAnalyticsDatasetValue('analyticsPlatformFilter', 'Todos')),
    actorSlugs: hasActor ? [analyticsRuntimeState.actorSlug] : [],
    relations: hasRelation && !hasActor ? [analyticsRuntimeState.relation] : [],
    relationPairs: hasActor && hasRelation ? [`${analyticsRuntimeState.actorSlug}:${analyticsRuntimeState.relation}`] : [],
    refreshTotal,
  });
  if (outcome === 'failed') showMiniToast('Nao foi possivel carregar a pagina agora', 'ti-alert-triangle');
  renderAnalyticsFeedCards(analyticsListedMentions());
  applyAnalyticsFeedFilters();
  renderAnalyticsFeedPagination();
}

/** Redesenha a nav a partir da pagina viva e religa os controles. */
function renderAnalyticsFeedPagination(): void {
  const navHost = qs<HTMLElement>('#analyticsFeedPagination');
  const feedPanel = navHost?.parentElement ?? qs<HTMLElement>('.u-analytics-feed-panel');
  if (!feedPanel) return;
  const markup = buildAnalyticsFeedPaginationMarkup(analyticsFeedPageView());
  if (navHost) navHost.outerHTML = markup;
  else feedPanel.insertAdjacentHTML('beforeend', markup);
  bindAnalyticsFeedPageControls();
}

/** Setas e campo de salto: cada um pede a pagina ao servidor. */
function bindAnalyticsFeedPageControls(): void {
  const currentPage = analyticsFeedPageView().page;
  qsa<HTMLButtonElement>('[data-analytics-feed-page]').forEach((pageButton) => {
    pageButton.addEventListener('click', () => {
      const step = pageButton.dataset.analyticsFeedPage === 'next' ? 1 : -1;
      void refreshAnalyticsFeedPage(Math.max(1, currentPage + step), false);
    });
  });
  qs<HTMLInputElement>('[data-analytics-feed-page-input]')?.addEventListener('change', (changeEvent) => {
    const asked = Number.parseInt((changeEvent.target as HTMLInputElement).value, 10);
    void refreshAnalyticsFeedPage(Number.isFinite(asked) ? Math.max(1, asked) : currentPage, false);
  });
}

/** Busca o recorte ativo (personagem × tipo × canal) no servidor e re-renderiza. */
async function refetchAnalyticsActorFeed(): Promise<void> {
  if (analyticsRuntimeState.actorFeedLoading) return;
  const activePlatform = activeAnalyticsDatasetValue('analyticsPlatformFilter', 'Todos');
  const hasActor = analyticsRuntimeState.actorSlug !== 'Todos';
  const hasRelation = analyticsRuntimeState.relation !== 'all';
  analyticsRuntimeState.actorFeedLoading = true;
  const actorMentions = await loadAnalyticsActorFeed({
    actorSlugs: hasActor ? [analyticsRuntimeState.actorSlug] : [],
    // personagem+tipo juntos filtram pelo PAR exato (omar-aziz:mencao) — a
    // relacao pertence AQUELE ator, nao a qualquer ator do card.
    relations: hasRelation && !hasActor ? [analyticsRuntimeState.relation] : [],
    relationPairs: hasActor && hasRelation ? [`${analyticsRuntimeState.actorSlug}:${analyticsRuntimeState.relation}`] : [],
    rawPlatforms: analyticsRawPlatformsForUiLabel(activePlatform),
  });
  analyticsRuntimeState.actorFeedLoading = false;
  if (!actorMentions) {
    showMiniToast('Nao foi possivel carregar o recorte agora', 'ti-alert-triangle');
    return;
  }
  analyticsRuntimeState.feedDrilled = true;
  renderAnalyticsFeedCards(actorMentions);
  applyAnalyticsFeedFilters();
}

/** Substitui os cards do feed + detalhes pre-renderizados e religa os eventos. */
function renderAnalyticsFeedCards(mentions: readonly OmarAnalyticsMention[]): void {
  const feedEl = qs<HTMLElement>('.u-analytics-feed');
  const detailEl = qs<HTMLElement>('#analyticsMentionDetail');
  /* v8 ignore next -- pagina montada sempre tem feed e detail shell (guard defensivo) */
  if (!feedEl || !detailEl) return;
  closeAnalyticsMentionDetail();
  feedEl.innerHTML = buildAnalyticsFeedCardsMarkup(mentions);
  detailEl.innerHTML = buildAnalyticsDetailCardsMarkup(mentions);
  for (const scopeEl of [feedEl, detailEl]) {
    bindAnalyticsMentionDetails(scopeEl);
    qsa<HTMLButtonElement>('[data-analytics-local-action]', scopeEl).forEach(bindAnalyticsLocalActionButton);
    qsa<HTMLButtonElement>('[data-analytics-block-author]', scopeEl).forEach(bindAnalyticsBlockAuthorButton);
  }
}

/** "Limpar filtros" REAL: volta canal/personagem/sentimento/busca ao padrao. */
function bindAnalyticsClearFiltersButton(clearButton: HTMLButtonElement): void {
  clearButton.addEventListener('click', () => {
    syncAnalyticsButtonsByValue('data-analytics-platform-filter', 'Todos');
    syncAnalyticsButtonsByValue('data-analytics-actor-filter', 'Todos');
    syncAnalyticsButtonsByValue('data-analytics-relation-filter', 'all');
    // Escopo/periodo NAO sao filtros: sao o recorte pedido ao servidor — ficam.
    syncAnalyticsButtonsByValue('data-analytics-sentiment-filter', 'all');
    const searchInput = qs<HTMLInputElement>('#analyticsSearchInput');
    if (searchInput) searchInput.value = '';
    analyticsRuntimeState.actorSlug = 'Todos';
    analyticsRuntimeState.relation = 'all';
    onAnalyticsFeedScopeChange();
    applyAnalyticsTableFilters();
    showMiniToast('Filtros limpos', 'ti-filter-off');
  });
}

function bindAnalyticsSentimentButton(sentimentButton: HTMLButtonElement): void {
  sentimentButton.addEventListener('click', () => {
    syncAnalyticsButtonsByValue('data-analytics-sentiment-filter', sentimentButton.dataset.analyticsSentimentFilter);
    applyAnalyticsFeedFilters();
    applyAnalyticsTableFilters();
  });
}

function bindAnalyticsSiteKindButton(siteKindButton: HTMLButtonElement): void {
  siteKindButton.addEventListener('click', () => {
    syncAnalyticsButtonsByValue('data-analytics-site-kind-filter', siteKindButton.dataset.analyticsSiteKindFilter);
    applyAnalyticsTableFilters();
  });
}

function bindAnalyticsMentionDetails(root: ParentNode = document): void {
  qsa<HTMLButtonElement>('[data-analytics-open-mention]', root).forEach((mentionButton) => {
    mentionButton.addEventListener('click', () => openAnalyticsMentionDetail(mentionButton.dataset.analyticsOpenMention));
  });
  qsa<HTMLButtonElement>('[data-analytics-back]', root).forEach((backButton) => {
    backButton.addEventListener('click', closeAnalyticsMentionDetail);
  });
}

function bindAnalyticsModuleShortcuts(): void {
  qsa<HTMLButtonElement>('[data-analytics-module-shortcut]').forEach((shortcutButton) => {
    shortcutButton.addEventListener('click', () => {
      const moduleName = parseAnalyticsModuleName(shortcutButton.dataset.analyticsModuleShortcut);
      if (moduleName) showAnalyticsModule(moduleName);
    });
  });
}

function bindAnalyticsExpandedRows(): void {
  qsa<HTMLButtonElement>('[data-analytics-expand-row]').forEach((expandButton) => {
    expandButton.addEventListener('click', () => toggleAnalyticsExpandedRow(expandButton));
  });
}

function bindAnalyticsPanelToggles(): void {
  qsa<HTMLButtonElement>('[data-analytics-toggle-panel]').forEach((toggleButton) => {
    toggleButton.addEventListener('click', () => {
      toggleAnalyticsPanel(toggleButton.dataset.analyticsTogglePanel);
    });
  });
  qs<HTMLButtonElement>('[data-analytics-filter-drawer-open]')?.addEventListener('click', openAnalyticsFilterDrawer);
  qsa<HTMLButtonElement>('[data-analytics-filter-drawer-close]').forEach((closeButton) => {
    closeButton.addEventListener('click', closeAnalyticsFilterDrawer);
  });
}

function bindAnalyticsLocalActions(): void {
  qs<HTMLButtonElement>('#analyticsExportBtn')?.addEventListener('click', () => showAnalyticsToast('Leitura executiva pronta para exportacao', 'ti-download'));
  // "Filtrar com IA" DESABILITADO (pedido do usuario 2026-07-26): era toast que
  // fingia filtrar — o botao nasce disabled no shell e o listener saiu.
  // O modal "Personalizar resumo" saiu em 2026-07-27 (aba digest virou os
  // Cards de Inteligencia Tatica) — bindings de customize/save/close removidos.
  qsa<HTMLButtonElement>('[data-analytics-local-chip]').forEach(bindAnalyticsLocalChip);
  qsa<HTMLButtonElement>('[data-analytics-local-action]').forEach(bindAnalyticsLocalActionButton);
}

function bindAnalyticsLocalActionButton(actionButton: HTMLButtonElement): void {
  actionButton.addEventListener('click', () => {
    showAnalyticsToast(actionButton.dataset.analyticsLocalAction ?? 'Acao registrada', 'ti-circle-check');
  });
}

function bindAnalyticsLocalChip(chipButton: HTMLButtonElement): void {
  chipButton.addEventListener('click', () => {
    chipButton.classList.toggle('is-active');
    chipButton.setAttribute('aria-pressed', chipButton.classList.contains('is-active') ? 'true' : 'false');
  });
}

function showAnalyticsModule(moduleName: OmarAnalyticsModuleName): void {
  analyticsRuntimeState.moduleName = moduleName;
  closeAnalyticsMentionDetail();
  setAnalyticsModuleButtons(moduleName);
  setAnalyticsModulePanels(moduleName);
  setAnalyticsFilterPanels(moduleName);
  closeAnalyticsFilterDrawer();
  applyAnalyticsTableFilters();
}

function setAnalyticsModuleButtons(moduleName: OmarAnalyticsModuleName): void {
  qsa<HTMLButtonElement>('[data-analytics-module]').forEach((moduleButton) => {
    const isActive = moduleButton.dataset.analyticsModule === moduleName;
    moduleButton.classList.toggle('is-active', isActive);
    moduleButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });
}

function setAnalyticsModulePanels(moduleName: OmarAnalyticsModuleName): void {
  qsa<HTMLElement>('[data-analytics-module-view]').forEach((moduleView) => {
    const isActive = moduleView.dataset.analyticsModuleView === moduleName;
    moduleView.hidden = !isActive;
    moduleView.classList.toggle('is-active', isActive);
  });
}

function setAnalyticsFilterPanels(moduleName: OmarAnalyticsModuleName): void {
  qsa<HTMLElement>('[data-analytics-filter-panel]').forEach((filterPanel) => {
    const isActive = filterPanel.dataset.analyticsFilterPanel === moduleName;
    filterPanel.hidden = !isActive;
    filterPanel.classList.toggle('is-active', isActive);
  });
}

function openAnalyticsMentionDetail(mentionId: string | undefined): void {
  const detailCardEl = mentionId ? qs<HTMLElement>(`[data-analytics-detail="${mentionId}"]`) : null;
  if (!detailCardEl) return;
  analyticsRuntimeState.moduleName = 'mentions';
  analyticsRuntimeState.detailMentionId = mentionId ?? '';
  setAnalyticsModulePanels('mentions');
  showAnalyticsDetailCard(detailCardEl);
  setAnalyticsModuleButtons('mentions');
  setAnalyticsFilterPanels('mentions');
  scrollAnalyticsMainToTop();
}

function showAnalyticsDetailCard(detailCardEl: HTMLElement): void {
  qs<HTMLElement>('#analyticsModuleViews')?.setAttribute('hidden', '');
  const detailShellEl = qs<HTMLElement>('#analyticsMentionDetail');
  if (detailShellEl) showAnalyticsMotionElement(detailShellEl);
  qsa<HTMLElement>('[data-analytics-detail]').forEach((cardEl) => {
    cardEl.hidden = cardEl !== detailCardEl;
  });
}

function closeAnalyticsMentionDetail(): void {
  analyticsRuntimeState.detailMentionId = '';
  qs<HTMLElement>('#analyticsModuleViews')?.removeAttribute('hidden');
  const detailShellEl = qs<HTMLElement>('#analyticsMentionDetail');
  if (detailShellEl) hideAnalyticsMotionElement(detailShellEl);
  qsa<HTMLElement>('[data-analytics-detail]').forEach((cardEl) => {
    cardEl.hidden = true;
  });
}

function toggleAnalyticsExpandedRow(expandButton: HTMLButtonElement): void {
  const rowId = expandButton.dataset.analyticsExpandRow;
  const expandedRowEl = rowId ? qs<HTMLElement>(`[data-analytics-expanded-row="${rowId}"]`) : null;
  if (!expandedRowEl) return;
  const isOpening = expandedRowEl.hidden;
  closeAnalyticsExpandedRows();
  if (isOpening) showAnalyticsMotionElement(expandedRowEl);
  expandButton.setAttribute('aria-expanded', isOpening ? 'true' : 'false');
  expandButton.classList.toggle('is-expanded', isOpening);
}

function closeAnalyticsExpandedRows(): void {
  qsa<HTMLElement>('[data-analytics-expanded-row]:not([hidden])').forEach(hideAnalyticsMotionElement);
  qsa<HTMLButtonElement>('[data-analytics-expand-row]').forEach(resetAnalyticsExpandedButton);
}

function resetAnalyticsExpandedButton(expandButton: HTMLButtonElement): void {
  expandButton.setAttribute('aria-expanded', 'false');
  expandButton.classList.remove('is-expanded');
}

function toggleAnalyticsPanel(panelName: string | undefined): void {
  if (panelName !== 'left' && panelName !== 'right') return;
  const analyticsPageEl = qs<HTMLElement>('#u-analytics-page');
  if (!analyticsPageEl) return;
  const className = panelClassName(panelName);
  analyticsPageEl.classList.toggle(className);
  updatePanelToggleLabel(panelName, analyticsPageEl.classList.contains(className));
}

function panelClassName(panelSide: AnalyticsPanelSide): string {
  return panelSide === 'left' ? 'is-left-collapsed' : 'is-right-collapsed';
}

function updatePanelToggleLabel(panelSide: AnalyticsPanelSide, isCollapsed: boolean): void {
  const targetLabel = panelSide === 'left' ? 'menu esquerdo' : 'filtros';
  const label = isCollapsed ? `Expandir ${targetLabel}` : `Recolher ${targetLabel}`;
  qs<HTMLButtonElement>(`[data-analytics-toggle-panel="${panelSide}"]`)?.setAttribute('aria-label', label);
}

function openAnalyticsFilterDrawer(): void {
  const analyticsPageEl = qs<HTMLElement>('#u-analytics-page');
  const mobileScrimEl = qs<HTMLElement>('.u-analytics-mobile-scrim');
  if (mobileScrimEl) showAnalyticsMotionElement(mobileScrimEl);
  analyticsPageEl?.classList.add('is-filter-drawer-open');
  qs<HTMLElement>('[data-analytics-filter-drawer-close]')?.removeAttribute('hidden');
}

function closeAnalyticsFilterDrawer(): void {
  const analyticsPageEl = qs<HTMLElement>('#u-analytics-page');
  const mobileScrimEl = qs<HTMLElement>('.u-analytics-mobile-scrim');
  analyticsPageEl?.classList.remove('is-filter-drawer-open');
  if (mobileScrimEl) hideAnalyticsMotionElement(mobileScrimEl);
}

/** Cards da LISTA de Mencoes. Escopado de proposito (2026-09-20): o modulo
 *  Destaques renderiza os mesmos cards com o mesmo atributo, e uma consulta
 *  global contava/escondia os dois conjuntos de uma vez. A CURADORIA (bloquear
 *  autor, fora de escopo) segue global — ali remover dos dois e o certo. */
function analyticsListedFeedCards(): HTMLElement[] {
  return qsa<HTMLElement>('[data-analytics-feed]', qs<HTMLElement>('.u-analytics-feed') ?? document);
}

function applyAnalyticsFeedFilters(): void {
  const filterState = readAnalyticsFilterState();
  let visibleCount = 0;
  analyticsListedFeedCards().forEach((mentionEl) => {
    const isVisible = analyticsMentionMatches(mentionEl, filterState);
    mentionEl.hidden = !isVisible;
    if (isVisible) visibleCount += 1;
  });
  updateAnalyticsFeedMeta(visibleCount, filterState);
}

function readAnalyticsFilterState(): AnalyticsFilterState {
  return {
    platform: activeAnalyticsDatasetValue('analyticsPlatformFilter', 'Todos'),
    actor: activeAnalyticsDatasetValue('analyticsActorFilter', 'Todos'),
    relation: activeAnalyticsDatasetValue('analyticsRelationFilter', 'all'),
    sentiment: activeAnalyticsDatasetValue('analyticsSentimentFilter', 'all'),
    siteKind: activeAnalyticsDatasetValue('analyticsSiteKindFilter', 'all'),
    search: qs<HTMLInputElement>('#analyticsSearchInput')?.value.trim().toLowerCase() ?? '',
  };
}

function analyticsMentionMatches(mentionEl: HTMLElement, filterState: AnalyticsFilterState): boolean {
  const platform = mentionEl.dataset.analyticsPlatform ?? '';
  const sentiment = mentionEl.dataset.analyticsSentiment ?? '';
  const cardActors = (mentionEl.dataset.analyticsActors ?? '').split('|');
  const cardRelations = (mentionEl.dataset.analyticsRelations ?? '').split('|');
  const matchesPlatform = filterState.platform === 'Todos' || platform === filterState.platform;
  const matchesActor = filterState.actor === 'Todos' || cardActors.includes(filterState.actor);
  const matchesRelation = filterState.relation === 'all' || cardRelations.includes(filterState.relation);
  const matchesSentiment = filterState.sentiment === 'all' || sentiment === filterState.sentiment;
  const matchesSearch = mentionEl.textContent?.toLowerCase().includes(filterState.search) ?? false;
  return matchesPlatform && matchesActor && matchesRelation && matchesSentiment && matchesSearch;
}

function applyAnalyticsTableFilters(): void {
  const filterState = readAnalyticsFilterState();
  qsa<HTMLElement>('[data-analytics-influencer-row]').forEach((tableRow) => {
    tableRow.hidden = !analyticsSentimentMatches(tableRow, filterState);
  });
  qsa<HTMLElement>('[data-analytics-site-row]').forEach((tableRow) => {
    tableRow.hidden = !analyticsSiteRowMatches(tableRow, filterState);
  });
}

function analyticsSentimentMatches(tableRow: HTMLElement, filterState: AnalyticsFilterState): boolean {
  const rowSentiment = tableRow.dataset.analyticsRowSentiment ?? '';
  return filterState.sentiment === 'all' || rowSentiment === filterState.sentiment;
}

function analyticsSiteRowMatches(tableRow: HTMLElement, filterState: AnalyticsFilterState): boolean {
  const rowKind = tableRow.dataset.analyticsSiteKind ?? '';
  const matchesKind = filterState.siteKind === 'all' || rowKind === filterState.siteKind;
  return analyticsSentimentMatches(tableRow, filterState) && matchesKind;
}

function updateAnalyticsFeedMeta(visibleCount: number, filterState: AnalyticsFilterState): void {
  const filterLabelEl = qs<HTMLElement>('#analyticsActiveFilterLabel');
  const feedCountEl = qs<HTMLElement>('#analyticsFeedCount');
  const channelLabel = filterState.platform === 'Todos' ? 'Todos os canais' : filterState.platform;
  const actorSuffix = filterState.actor === 'Todos' ? '' : ` · ${analyticsActorNameForSlug(filterState.actor)}`;
  // Cabecalho nomeia o escopo SERVIDO (2026-09-02) — so no live, onde ele existe.
  const scopeSuffix = analyticsLiveSource() === 'live' ? ` · ${ANALYTICS_SCOPE_LABELS[getAnalyticsViewState().scope]}` : '';
  if (filterLabelEl) filterLabelEl.textContent = `${channelLabel}${actorSuffix}${scopeSuffix}`;
  // Denominador via DOM: cards de autor bloqueado sao REMOVIDOS em runtime.
  if (feedCountEl) feedCountEl.textContent = `${visibleCount}/${analyticsListedFeedCards().length}`;
  markAnalyticsFeedAsExample(filterLabelEl);
}

/** Live que voltou VAZIO segue mostrando os cards de exemplo — dizer isso e melhor
 * que o operador achar que o recorte tem conteudo real (guarda do escopo 017/018). */
function markAnalyticsFeedAsExample(filterLabelEl: HTMLElement | null): void {
  if (!filterLabelEl || !analyticsEmptyLiveFields().includes('feedItems')) return;
  filterLabelEl.textContent = `${filterLabelEl.textContent ?? ''} · sem conteudo ao vivo no recorte (exemplo)`;
}

/** Nome exibível do personagem (chips carregam o slug como chave estável). */
function analyticsActorNameForSlug(actorSlug: string): string {
  /* v8 ignore next -- chips vem dos proprios totais (find nunca falha p/ chip clicavel); ?? exigido pelo optional chaining */
  return getAnalyticsActorTotals().find((actorTotal) => actorTotal.actorSlug === actorSlug)?.actorName ?? actorSlug;
}

function parseAnalyticsModuleName(moduleName: string | undefined): OmarAnalyticsModuleName | null {
  const allowedNames = OMAR_ANALYTICS_MODULES.map((analyticsModule) => analyticsModule.moduleName);
  return allowedNames.includes(moduleName as OmarAnalyticsModuleName) ? (moduleName as OmarAnalyticsModuleName) : null;
}

function scrollAnalyticsMainToTop(): void {
  const analyticsMainEl = qs<HTMLElement>('.u-analytics-main');
  if (analyticsMainEl && typeof analyticsMainEl.scrollTo === 'function') {
    analyticsMainEl.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

function showAnalyticsToast(message: string, icon: string): void {
  showMiniToast(message, icon);
}
